import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@environment';
import { BrandType, HttpListResponse } from '@ess-front/shared';
import { TalkJsUserRole } from '@shared/models/enum/talkjs-user-role.enum';
import { ChoiceBE } from '@shared/models/http/choice-be.model';
import { Choice } from '@shared/models/http/choice.model';
import { HttpOptionsResponse } from '@shared/models/http/http-options-response.model';
import { RelativeOptionsBE } from '@shared/models/http/relative-options-be.model';
import { RelativeOptions } from '@shared/models/http/relative-options.model';
import { AddTravelerDataBE } from '@shared/models/itinerary/checkout/add-traveler-data-be.interface';
import { AddTravelerData } from '@shared/models/itinerary/checkout/add-traveler-data.interface';
import { MemberExperienceManager } from '@shared/models/user/member-experience-manager.model';
import { RelativeBE } from '@shared/models/user/relative-be.model';
import { Relative } from '@shared/models/user/relative.model';
import { MemberExperienceManagerBE, UserBE } from '@shared/models/user/user-be.model';
import { User } from '@shared/models/user/user.model';
import { Observable, map } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private readonly apiURL = `${environment.apiEnv}api/`;

  constructor(private httpClient: HttpClient) {}

  getUser$(): Observable<User> {
    const url = `${this.apiURL}users/me/`;
    return this.httpClient.get<UserBE>(url).pipe(map(user => this.transformIntoUser(user)));
  }

  getUserByHash$(hash: string): Observable<User> {
    const url = `${this.apiURL}v2/users/${hash}/`;
    return this.httpClient.get<UserBE>(url).pipe(map(user => this.transformIntoUser(user, hash)));
  }

  patchUser$(user: Partial<User>): Observable<User> {
    const url = `${this.apiURL}users/me/`;
    const userBe = {
      preferences: user.preferences,
      travel_habits: user.travelHabits,
      categories: user.categories,
      is_onboarding_done: user.isOnboardingDone,
    };

    return this.httpClient.patch<UserBE>(url, userBe).pipe(map(user => this.transformIntoUser(user)));
  }

  getRelatives$(): Observable<Relative[]> {
    const url = `${this.apiURL}users/me/relatives`;
    return this.httpClient
      .get<HttpListResponse<RelativeBE>>(url)
      .pipe(map(rs => this.transformIntoRelatives(rs.results)));
  }

  getRelativesOptions$(): Observable<RelativeOptions> {
    const url = `${this.apiURL}users/me/relatives`;
    return this.httpClient
      .options<HttpOptionsResponse<{ POST: RelativeOptionsBE }>>(url)
      .pipe(map(rs => this.transformIntoRelativesOptions(rs.actions.POST)));
  }

  addNewTraveler(newTraveler: AddTravelerData): Observable<AddTravelerDataBE> {
    const url = `${this.apiURL}users/me/relatives`;
    const formData = this.transformIntoNewTravelerFormData(newTraveler);
    return this.httpClient.post<AddTravelerDataBE>(url, formData);
  }

  private transformIntoUser(user: UserBE, hash: string | null = null): User {
    return {
      avatar: user.avatar,
      chatSignature: user.chat_signature,
      email: user.email,
      firstName: user.first_name,
      id: user.id,
      lastName: user.last_name,
      name: `${user.first_name} ${user.last_name}`.trim(),
      memberExperienceManager:
        user.member_experience_manager && this.transformIntoMemberExperienceManager(user.member_experience_manager),
      memberSince: user.member_since,
      brand: user.brand || BrandType.ESSENTIALIST,
      talkJsRole: TalkJsUserRole.USER,
      hash: hash ?? user.hash,
      preferences: user.preferences,
      travelHabits: user.travel_habits,
      categories: user.categories,
      isOnboardingDone: user.is_onboarding_done,
    };
  }

  private transformIntoMemberExperienceManager(member: MemberExperienceManagerBE): MemberExperienceManager {
    return {
      avatar: member.avatar,
      calendarUrl: member.calendar_url,
      email: member.email,
      firstName: member.first_name,
      lastName: member.last_name,
      name: member.name,
    };
  }

  private transformIntoRelatives(relatives: RelativeBE[]): Relative[] {
    return relatives.map(relative => {
      return {
        avatar: relative.avatar,
        firstName: relative.first_name,
        lastName: relative.last_name,
        id: relative.id,
        invitedByRelationship: relative.invited_by_relationship,
      };
    });
  }

  private transformIntoRelativesOptions(options: RelativeOptionsBE): RelativeOptions {
    return {
      genderOptions: options.gender.choices.map(this.transformIntoChoice),
      phonePrefixOptions: options.phone_country_code.choices.map(this.transformIntoChoice),
      relationshipOptions: options.invited_by_relationship.choices.map(this.transformIntoChoice),
    };
  }

  private transformIntoChoice(choice: ChoiceBE): Choice {
    return {
      value: choice.value,
      displayName: choice.display_name,
    };
  }

  private transformIntoNewTravelerFormData(newTraveler: AddTravelerData): FormData {
    const addTravelerBeModel: AddTravelerDataBE = {
      avatar: newTraveler.image,
      birth_date: newTraveler.birthdate,
      country: newTraveler.country,
      email: newTraveler.email,
      first_name: newTraveler.firstName,
      middle_name: newTraveler.middleName,
      last_name: newTraveler.lastName,
      gender: newTraveler.gender,
      invited_by_relationship: newTraveler.relationship,
      passport_expiry_date: newTraveler.expireDate,
      passport_number: newTraveler.passportNumber,
      phone: newTraveler.telephone,
      phone_country_code: newTraveler.prefix,
    };

    const fd = new FormData();
    Object.entries(addTravelerBeModel).forEach(entry => {
      const [key, value] = entry;
      fd.set(key, value);
    });
    return fd;
  }
}
